import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@component/card";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@component/table";
import { getAdminHiringJobMetrics } from "@service/admin";
import {
    AdminHiringDateRange,
    AdminHiringJobMetricsRow,
} from "@type/admin";
import { catchErrorLog } from "@util/sentry";
import { AlertCircleIcon, BriefcaseIcon } from "lucide-react";

interface Props {
    companyId: string;
    range: AdminHiringDateRange;
}

function formatPercent(value: number | undefined): string {
    if (value === undefined || !Number.isFinite(value)) return "0%";
    return `${value.toFixed(0)}%`;
}

function formatMinutes(value: number | undefined): string {
    if (value === undefined || !Number.isFinite(value)) return "—";
    return `${value.toFixed(0)} min`;
}

export default function JobsTable({ companyId, range }: Props) {
    const [rows, setRows] = useState<AdminHiringJobMetricsRow[] | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>("");

    useEffect(() => {
        if (!companyId) {
            setRows(null);
            setError("");
            setIsLoading(false);
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        setError("");
        getAdminHiringJobMetrics({ company_id: companyId, range })
            .then((resp) => {
                if (cancelled) return;
                setRows(resp.data?.data?.jobs ?? []);
            })
            .catch((err) => {
                if (cancelled) return;
                catchErrorLog(`Hiring Activity: failed to fetch job metrics: ${err}`);
                setError(
                    axios.isAxiosError(err)
                        ? (err.response?.data as { message?: string } | undefined)?.message
                            || "Failed to load per-job metrics."
                        : "Failed to load per-job metrics."
                );
                setRows([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [companyId, range]);

    if (error) {
        return (
            <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4 flex items-start gap-3">
                    <AlertCircleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">{error}</div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent className="p-0">
                <div className="px-5 py-4 border-b border-neutral-100">
                    <div className="flex items-center gap-2">
                        <BriefcaseIcon className="w-4 h-4 text-blue-600" />
                        <h3 className="text-sm font-semibold text-gray-900">
                            Per-job breakdown
                        </h3>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-[28%]">Job Name</TableHead>
                                <TableHead>AI Screening Completion Rate</TableHead>
                                <TableHead>AI Interview Drop-off Rate</TableHead>
                                <TableHead>Median AI Screening Duration</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                Array.from({ length: 3 }).map((_, i) => (
                                    <TableRow key={`skeleton-${i}`}>
                                        <TableCell>
                                            <div className="h-4 w-3/4 bg-neutral-100 rounded animate-pulse" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="h-4 w-24 bg-neutral-100 rounded animate-pulse" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="h-4 w-24 bg-neutral-100 rounded animate-pulse" />
                                        </TableCell>
                                        <TableCell>
                                            <div className="h-4 w-24 bg-neutral-100 rounded animate-pulse" />
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : !rows || rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className="text-center text-sm text-neutral-500 py-10">
                                        No job postings found for this period.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map((row) => (
                                    <TableRow key={row.job_id}>
                                        <TableCell className="font-medium text-gray-900">
                                            {row.job_title || "—"}
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm font-semibold text-gray-900 tabular-nums">
                                                {formatPercent(row.assessment_completion_rate)}
                                            </div>
                                            <div className="text-[11px] text-neutral-500 tabular-nums">
                                                {row.assessments_completed} completed | {row.assessments_pending} pending
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm font-semibold text-gray-900 tabular-nums">
                                                {formatPercent(row.drop_off_rate)}
                                            </div>
                                            <div className="text-[11px] text-neutral-500 tabular-nums">
                                                {row.interviews_partial} partial interview | {row.interviews_aborted} aborted interview
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-sm font-semibold text-gray-900 tabular-nums">
                                                {formatMinutes(row.median_assessment_duration_minutes)}
                                            </div>
                                            <div className="text-[11px] text-neutral-500 tabular-nums">
                                                min: {formatMinutes(row.min_assessment_duration_minutes)} | max: {formatMinutes(row.max_assessment_duration_minutes)}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>
    );
}
